import { isEqual } from 'lodash-es';
import {
  readAndCheckFile,
  checkTypeAndExtensionMatches,
  FILE_TYPE_MISMATCH_ERROR,
} from 'platform/forms-system/src/js/utilities/file';
import { SUPPLEMENTAL_CLAIM_DOC_TYPES_FALLBACK } from './supplementalClaimDocTypes';

export const FILE_TYPES = ['pdf', 'gif', 'jpeg', 'jpg', 'bmp', 'txt'];

// The Claims Evidence API (used for Supplemental Claims file uploads)
// accepts different file types than Benefits Documents
export const CE_FILE_TYPES = [
  'png',
  'pdf',
  'tif',
  'tiff',
  'txt',
  'jpeg',
  'jpg',
  'bmp',
];

// File validation constants
export const MAX_FILE_SIZE_MB = 50;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 ** 2;
export const MAX_PDF_SIZE_MB = 99;
export const MAX_PDF_SIZE_BYTES = MAX_PDF_SIZE_MB * 1024 ** 2;
export const MAX_SC_FILE_SIZE_MB = 20;
export const MAX_SC_FILE_SIZE_BYTES = MAX_SC_FILE_SIZE_MB * 1024 ** 2;

// Error messages
export const FILE_SIZE_ERROR_PDF = `The file you selected is larger than the ${MAX_PDF_SIZE_MB}MB maximum file size and could not be added.`;
export const FILE_SIZE_ERROR_NON_PDF = `The file you selected is larger than the ${MAX_FILE_SIZE_MB}MB maximum file size and could not be added.`;
// Specific for Supplemental Claim file uploads
export const FILE_SIZE_ERROR_SC = `File must be under ${MAX_SC_FILE_SIZE_MB}MB`;

// Helper functions
export const isPdf = file => file.name?.toLowerCase().endsWith('.pdf') ?? false;

export const validateFileSize = (file, usingClaimsEvidenceAPI = false) => {
  const pdf = isPdf(file);
  // Max Benefits Documents file size (used for non-Supplemental Claim file uploads)
  const maxBDFileSize = pdf ? MAX_PDF_SIZE_BYTES : MAX_FILE_SIZE_BYTES;
  // Max Claims Evidence file size (used for Supplemental Claims only)
  const maxCEFileSize = MAX_SC_FILE_SIZE_BYTES;

  if (usingClaimsEvidenceAPI && file.size > maxCEFileSize) {
    return FILE_SIZE_ERROR_SC;
  }

  if (!usingClaimsEvidenceAPI && file.size > maxBDFileSize) {
    return pdf ? FILE_SIZE_ERROR_PDF : FILE_SIZE_ERROR_NON_PDF;
  }

  return null; // Valid
};

/**
 * ALREADY_SUBMITTED: in evidenceSubmissions (files received list below the uploader)
 * SAME_BATCH: in the current upload queue
 */
export const DUPLICATE_REASON = {
  ALREADY_SUBMITTED: 'already-submitted',
  SAME_BATCH: 'same-batch',
};

/**
 * Given a file that is attempting to be uploaded, all the files currently in queue for upload,
 * their doc types, and the claim or appeal's previously uploaded files, compare the file's:
 * 1) name
 * 2) extension
 * 3) size
 * 4) document type (e.g. Buddy/Lay Statement)
 *
 * to determine whether the file is a duplicate of something that has already
 * been uploaded, or a duplicate within the current upload queue
 *
 * @param {*} currentFile - Individual file currently being evaluated
 * @param {*} files - Array of all files currently being uploaded
 * @param {*} index - Index of the current file in the files array
 * @param {*} docTypes - Array of document types corresponding to each file
 * @param {*} evidenceSubmissions - Previously uploaded files
 * @returns {string|null} A `DUPLICATE_REASON` value, or null if not a duplicate
 */
export const checkForDuplicates = (
  currentFile,
  files,
  currentFileIndex,
  docTypes,
  evidenceSubmissions,
) => {
  let duplicateInEvidenceSubmissions = false;
  let duplicateInCurrentQueue = false;

  const getFileExtension = fileName =>
    fileName?.split('.').pop()?.toLowerCase() ?? '';

  const getDocType = docTypeValue =>
    SUPPLEMENTAL_CLAIM_DOC_TYPES_FALLBACK.find(
      doc => doc.value === docTypeValue,
    )?.label || '';

  // Gather all comparison values for the current file
  const currentFileMeta = {
    name: currentFile.name,
    extension: getFileExtension(currentFile.name),
    size: currentFile.size,
    documentType: getDocType(docTypes[currentFileIndex]),
  };

  // See if files earlier in the current queue match the one we're evaluating
  const fileNameMatchesInCurrentQueue = files
    .map((fileInQueue, i) => ({ fileInQueue, originalIndex: i }))
    .filter(
      ({ fileInQueue, originalIndex }) =>
        fileInQueue.file.name === currentFile.name &&
        originalIndex < currentFileIndex,
    );

  if (fileNameMatchesInCurrentQueue?.length) {
    duplicateInCurrentQueue = fileNameMatchesInCurrentQueue.some(
      ({ fileInQueue, originalIndex }) => {
        // Gather all comparison values for a potential match
        const potentialMatchMeta = {
          name: fileInQueue.file.name,
          extension: getFileExtension(fileInQueue.file.name),
          size: fileInQueue.file.size,
          documentType: getDocType(docTypes[originalIndex]),
        };

        // Return whether current file is a duplicate of another file earlier in the queue
        return isEqual(currentFileMeta, potentialMatchMeta);
      },
    );
  }

  const fileNameMatchesInES = (evidenceSubmissions || []).filter(
    submission => submission.fileName === currentFile.name,
  );

  if (fileNameMatchesInES?.length) {
    duplicateInEvidenceSubmissions = fileNameMatchesInES.some(match => {
      // Gather all comparison values for a potential match
      const potentialMatchMeta = {
        name: match.fileName,
        extension: getFileExtension(match.fileName),
        size: match.fileSize,
        documentType: match.documentType,
      };

      // Return whether current file is a duplicate of another file in evidenceSubmissions
      return isEqual(currentFileMeta, potentialMatchMeta);
    });
  }

  // Evidence submissions take precedence when a file is a duplicate for both
  // reasons (in DUPLICATE_REASON)
  if (duplicateInEvidenceSubmissions) {
    return DUPLICATE_REASON.ALREADY_SUBMITTED;
  }

  return duplicateInCurrentQueue ? DUPLICATE_REASON.SAME_BATCH : null;
};

/**
 * Validates a single file and returns any error message
 * @param {File} file - The file to validate
 * * @param {boolean} usingClaimsEvidenceAPI - Defaults to false. True for Supplemental Claim file uploads
 * @returns {Promise<string|null>} Error message or null if valid
 */
export const validateFile = async (file, usingClaimsEvidenceAPI = false) => {
  if (!file) {
    return null;
  }

  const sizeError = validateFileSize(file, usingClaimsEvidenceAPI);

  if (sizeError) {
    return sizeError;
  }

  // Check file extension matches content
  try {
    const checks = { checkTypeAndExtensionMatches };
    const checkResults = await readAndCheckFile(file, checks);

    if (!checkResults.checkTypeAndExtensionMatches) {
      return FILE_TYPE_MISMATCH_ERROR;
    }
  } catch (error) {
    // If we can't read the file, we'll let it through
    // and handle it during submission
  }

  return null;
};

/**
 * Validates multiple files in parallel
 * @param {Array} fileInfos - Array of file info objects with file property
 *
 * @param {boolean} usingClaimsEvidenceAPI - Defaults to false. If true (Supplemental
 * Claim file uploads), we have different error messaging for failed file uploads
 *
 * @returns {Promise<Array>} Array of validation results {index, error}
 */
export const validateFiles = async (
  fileInfos,
  usingClaimsEvidenceAPI = false,
) => {
  const validationPromises = fileInfos.map(async (fileInfo, index) => {
    if (!fileInfo?.file) return null;

    const error = await validateFile(fileInfo.file, usingClaimsEvidenceAPI);

    return error ? { index, error } : null;
  });

  const results = await Promise.all(validationPromises);

  return results.filter(result => result !== null);
};
